import Icon, { EditOutlined, UserOutlined } from '@ant-design/icons';
import {
    Avatar,
    Button,
    Checkbox,
    Col,
    DatePicker,
    Form,
    FormInstance,
    Image,
    Input,
    InputNumber,
    message,
    Row,
    Select,
    Space,
    Tabs,
    Upload,
} from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import moment from 'moment';
import TextArea from 'rc-textarea';
import React, { Component } from 'react';
import { FormattedDate, FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { RouteComponentProps } from 'react-router-dom';
import positionApi from '../../apis/PositionApi';
import sportApi from '../../apis/SportApi';
import userApi from '../../apis/UserApi';
import userSportApi from '../../apis/UserSportApi';
import LayoutComponent from '../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../context/CustomContext';
import { Position, Sport, User, UserAcademic, UserAccount, UserSport } from '../../model/Entities';
import { countries, CountryType, genders, shirtSizes } from '../../model/Types';
import { ReactComponent as saveSvg } from '../../resources/images/ico-save.svg';
import ga from '../../services/GoogleAnalytics';
import HeadMetadata from '../../services/HeadMetadata';
import metricService from '../../services/MetricService';
import notificationService from '../../services/NotificationService';
import playerService from '../../services/PlayerService';
import AddUserSportComponent from './AddUserSportComponent/AddUserSportComponent';
import styles from './PlayerEditPage.module.scss';
import avatar from '../../resources/images/profile-placeholder.png';
import stateService from '../../services/StateService';
import { CustomerSessionCreationV2 } from '../../model/v2/EntitiesV2';
import paymentApiV2 from '../../apis/v2/PaymentApiV2';

class PlayerEditPage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;
    userSportsFormRef = React.createRef<FormInstance>();
    userAcademicFormRef = React.createRef<FormInstance>();
    userAccountFormRef = React.createRef<FormInstance>();
    userEmailFormRef = React.createRef<FormInstance>();
    userUsernameFormRef = React.createRef<FormInstance>();

    constructor(props: Props) {
        super(props);
        this.state = {
            sports: [],
            sportPhotos: [],
            sportPositions: new Map(),
            userSports: [],
        };
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            this.setState({ loading: 'initializing' });

            // get player
            const userId = this.props.match.params.id;
            const responses = await Promise.all([
                playerService.getPlayer(this.props),
                sportApi.listAll(),
                userApi.getAcademic(userId),
                userApi.getAccount(userId),
            ]);
            const player = responses[0];
            const sports = responses[1];
            const userAcademic = responses[2];
            const userAccount = responses[3];

            // get sports and positions
            const sportPositions = await this.listSportsPositions(player.userSports);
            const sportPhotos: SportPhoto[] = player.userSports.map((us) => ({
                sportId: us.sportId!,
                userSportId: us.id!,
                photo: us.photoUrl,
            }));

            // load user sports form
            const userSportsValues = Object.assign({}, player.user, player.userSports);
            userSportsValues.userSports.forEach(
                (us: any) => (us.positions = player.userSports.find((usaux) => usaux.id === us.id)?.positions),
            );
            this.userSportsFormRef.current?.setFieldsValue(userSportsValues);

            // load user academic form
            const userAcademicValues = Object.assign({}, userAcademic);
            this.userAcademicFormRef.current?.setFieldsValue(userAcademicValues);

            // get country
            let country = userAccount.country;
            if (country && !countries.includes(country)) {
                country = undefined;
            }

            // load user account form
            const userAccountValues: any = Object.assign({}, userAccount);
            const height = userAccountValues.height;
            userAccountValues.height = metricService.toFeets(height);
            userAccountValues.heightInches = metricService.toInches(height);
            userAccountValues.privacyHeight = !userAccountValues.privacyHeight;
            userAccountValues.privacyWeight = !userAccountValues.privacyWeight;
            if (userAccountValues.birthday) {
                userAccountValues.birthday = moment.utc(userAccountValues.birthday).local();
            }
            userAccountValues.country = country;
            this.userAccountFormRef.current?.setFieldsValue(userAccountValues);

            // load user email form
            this.userEmailFormRef.current?.setFieldsValue({ email: userAccount.email });

            // load user username form
            this.userUsernameFormRef.current?.setFieldsValue({ userName: userAccount.userName });

            this.setState({ ...player, userAcademic, userAccount, sports, sportPositions, sportPhotos, country });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    listSportsPositions = async (userSports: UserSport[]): Promise<Map<number, Position[]>> => {
        const sportIds = userSports.map((us: UserSport) => us.sportId!);
        const sportPositions: Map<number, Position[]> = new Map();
        await Promise.all(
            sportIds.map(async (sportId) => {
                const positions = await positionApi.list(sportId);
                sportPositions.set(sportId, positions);
            }),
        );

        return sportPositions;
    };

    /**
     * Saves the users sports of the player.
     * @param values - the values
     */
    saveUserSports = async (values: any) => {
        const { sportPhotos } = this.state;
        try {
            this.setState({ loading: 'saving' });

            // upload photos
            sportPhotos.forEach(async (sp) => {
                if (sp.file && /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(sp.file.name)) {
                    await userSportApi.updatePhoto(this.state.user?.id!, sp.userSportId, sp.file as File);
                }
            });

            // save user sports
            const sportIds = this.state.user?.userSports.map((us) => us.sportId);
            let user: User = Object.assign({}, this.state.user, values);
            user.userSports = user.userSports.filter((us) => sportIds?.includes(us.sportId));
            user.userSports.forEach((us: any) => (us.jerseyNumber = us.jerseyNumbers));
            await Promise.all(user.userSports.map((us) => userSportApi.update(user, us)));
            message.success(this.props.intl.formatMessage({ id: 'status.saved' }));
            ga.createDefaultEvent('player profile save - sports', 'player profile save - sports - success');

            this.setState({ user });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
            ga.createDefaultEvent('player profile save - sports', 'player profile save - sports - error');
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /**
     * Saves the academic information of the player.
     * @param values - the values
     */
    saveUserAcademic = async (values: any) => {
        const { user } = this.state;

        try {
            this.setState({ loading: 'saving' });

            // save user academic
            let userAcademic: UserAcademic = Object.assign({}, this.state.userAcademic, values);
            userAcademic = await userApi.updateAcademic(user!.id!, userAcademic);
            message.success(this.props.intl.formatMessage({ id: 'status.saved' }));
            ga.createDefaultEvent('player profile save - academic', 'player profile save - academic - success');

            this.setState({ userAcademic });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
            ga.createDefaultEvent('player profile save - academic', 'player profile save - academic - error');
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /**
     * Saves the account information of the player.
     * @param values - the values
     */
    saveUserAccount = async (values: any) => {
        const { user } = this.state;

        try {
            this.setState({ loading: 'saving' });

            // save user account
            let userAccount: UserAccount = Object.assign({}, this.state.userAccount, values);
            userAccount.height = metricService.toTotalInches(values.height, values.heightInches);
            userAccount.privacyHeight = !userAccount.privacyHeight;
            userAccount.privacyWeight = !userAccount.privacyWeight;
            userAccount.birthday = values.birthday ? values.birthday.valueOf() : undefined;
            userAccount = await userApi.updateAccount(user!.id!, userAccount);
            message.success(this.props.intl.formatMessage({ id: 'status.saved' }));
            ga.createDefaultEvent('player profile save - account', 'player profile save - account - success');

            this.setState({ userAccount });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
            ga.createDefaultEvent('player profile save - account', 'player profile save - account - error');
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /**
     * Saves the user email of the player.
     * @param values - the values
     */
    saveUserEmail = async (values: any) => {
        const { user } = this.state;
        try {
            this.setState({ loading: 'savingEmail' });
            await userApi.updateEmail(user!.id!, values.email);
            message.success(this.props.intl.formatMessage({ id: 'status.saved' }));
        } catch (error) {
            notificationService.displayError(error, this.props.intl, [
                { status: 409, message: 'status.duplicatedEmail' },
            ]);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /**
     * Saves the username of the player.
     * @param values - the values
     */
    saveUsername = async (values: any) => {
        const { user } = this.state;
        try {
            this.setState({ loading: 'savingUsername' });
            await userApi.updateUsername(user!.id!, values.userName);
            message.success(this.props.intl.formatMessage({ id: 'status.saved' }));
        } catch (error) {
            notificationService.displayError(error, this.props.intl, [
                { status: 409, message: 'status.duplicatedUsername' },
            ]);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    showModalUserSport = (modalUserSportVisible: boolean) => {
        this.setState({ modalUserSportVisible });
    };

    addUserSports = async (userSport: UserSport) => {
        const { userSports } = this.state;
        try {
            const user = Object.assign({}, this.state.user);
            user.userSports.push(userSport);
            const sportPositions = await this.listSportsPositions(userSports!);
            this.setState({ user, sportPositions, userSport });
            this.props.history.push(`/players/${user!.id}/edit?sportId=${userSport?.sportId}`);
            const values: any = Object.assign({}, userSport);
            this.userSportsFormRef.current?.setFieldsValue(values);
            this.showModalUserSport(false);
            this.init();
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    deleteUserSport = async (userSport: UserSport) => {
        try {
            this.setState({ loading: 'deleting' });
            const user = Object.assign({}, this.state.user);
            await userSportApi.delete(user.id!, userSport.sportId!);
            user.userSports = user.userSports.filter((us) => us.id !== userSport.id);
            this.setState({ user });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    changeUserSport = (userSportId: number) => {
        const { userSports, user } = this.state;
        const userSport = userSports.find((u) => u.id === userSportId);
        this.props.history.push(`/players/${user!.id}/edit?sportId=${userSport?.sportId}`);
        this.setState({ userSport });
    };

    changeCountry = (country?: CountryType) => {
        this.userAccountFormRef.current?.resetFields(['state']);
        this.setState({ country });
    };

    uploadPhoto = (file: UploadFile, sportPhoto: SportPhoto) => {
        const sportPhotos = [...this.state.sportPhotos];
        this.getBase64(file, (photo: string) => {
            sportPhotos.find((sp) => sp.userSportId === sportPhoto.userSportId)!.photo = photo;
            sportPhotos.find((sp) => sp.userSportId === sportPhoto.userSportId)!.file = file;
            this.setState({ sportPhotos });
        });
        return false;
    };

    getBase64 = (img: any, callback: any) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => callback(reader.result));
        reader.readAsDataURL(img);
    };

    redirectToStripeCustomerPortal = async () => {
        try {
            this.setState({ loading: 'loadingCustomerSession' });

            const currentUrl = window.location.href;
            const customerSessionCreation: CustomerSessionCreationV2 = { returnUrl: currentUrl };
            const customerSession = await paymentApiV2.createCustomerSession(customerSessionCreation);
            window.location.href = customerSession.url;
        } catch (error: any) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    /*** COMPONENTS ***/

    renderHeaderDesktop = (desktop: boolean): React.ReactElement | undefined => {
        const { sport, user, userSport } = this.state;
        if (desktop) {
            return (
                <div className={styles.header}>
                    <div className={styles.avatarTop}>
                        <Avatar
                            className={styles.picture}
                            src={userSport?.photoUrl || avatar}
                            icon={<UserOutlined />}
                        />
                        <h1>
                            {user?.profile.givenName} {user?.profile.familyName}
                        </h1>
                    </div>
                    <div className={styles.headerBackground}>
                        <div className={styles.headerBackgroundGradient}></div>
                        <div
                            className={styles.headerBackgroundImage}
                            style={{ backgroundImage: `url(${sport?.backgroundUrl})` }}
                        ></div>
                    </div>
                    <nav>
                        <Button shape="round" className={styles.save} icon={<Icon component={saveSvg} />}>
                            <FormattedMessage id="button.save" />
                        </Button>
                    </nav>

                    <h1>
                        <FormattedMessage id="player.edit.header" />
                    </h1>
                </div>
            );
        }
    };

    renderHeaderMobile = (desktop: boolean): React.ReactElement | undefined => {
        const { sport } = this.state;

        if (!desktop) {
            return (
                <div className={styles.header}>
                    <div className={styles.mobileHeaderBackground}>
                        <div className={styles.headerBackgroundGradient}></div>
                        <div
                            className={styles.headerBackgroundImage}
                            style={{ backgroundImage: `url(${sport?.backgroundUrl})` }}
                        ></div>
                    </div>
                    <h1>
                        <FormattedMessage id="player.edit.header" />
                    </h1>
                </div>
            );
        }
    };

    renderSports = (): React.ReactElement | undefined => {
        const {
            user,
            sport,
            sports,
            userSports,
            sportPositions,
            modalUserSportVisible,
            sportPhotos,
            userSport,
            loading,
        } = this.state;

        const jerseyNumbers = Array.from({ length: 100 }, (x, i) => i);
        const jerseyNumbersOptions = jerseyNumbers.map((jn) => (
            <Select.Option key={jn} value={jn}>
                {jn}
            </Select.Option>
        ));

        return (
            <Form
                ref={this.userSportsFormRef}
                onFinish={this.saveUserSports}
                colon={false}
                layout="vertical"
                className={styles.form}
            >
                {user && (
                    <>
                        {sport && sports && modalUserSportVisible && (
                            <AddUserSportComponent
                                userId={user.id!}
                                onCancel={() => this.showModalUserSport(false)}
                                onUpdate={this.addUserSports}
                                sports={sports}
                                user={user}
                            />
                        )}

                        <Form.List name="userSports">
                            {(fields, { add, remove }) => (
                                <div className="sportsTabs">
                                    {fields.map(({ key, name, fieldKey, ...restField }, index) => {
                                        const userSportAux = userSports?.find(
                                            (us) => us.id === user?.userSports[key].id,
                                        )!;
                                        const sport = userSportAux && sports.find((s) => s.id === userSportAux.sportId);
                                        const sportPhoto =
                                            userSportAux &&
                                            sportPhotos.find((sp) => sp.userSportId === userSportAux.id)!;

                                        if (sport) {
                                            const positionOptions = sportPositions?.get(sport.id!)?.map((p) => (
                                                <Checkbox key={p.id} value={p.id}>
                                                    <span>{p.code}</span> {p.name}
                                                </Checkbox>
                                            ));

                                            return (
                                                <div hidden={userSportAux.id !== userSport?.id}>
                                                    <Row gutter={[40, 0]}>
                                                        <Col xs={24} lg={12}>
                                                            <div className={styles.avatar}>
                                                                <div className={styles.picture}>
                                                                    <Image
                                                                        src={sportPhoto?.photo}
                                                                        fallback="data:image/png;base64,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"
                                                                        preview={false}
                                                                    />
                                                                    <Upload.Dragger
                                                                        beforeUpload={(file) =>
                                                                            this.uploadPhoto(file, sportPhoto)
                                                                        }
                                                                        showUploadList={false}
                                                                        className={styles.upload}
                                                                    >
                                                                        <FormattedMessage id="admin.rosters.roster.updatePhoto" />
                                                                    </Upload.Dragger>
                                                                </div>
                                                            </div>
                                                            <Form.Item
                                                                label={<FormattedMessage id="player.edit.bio" />}
                                                                name={[name, 'bio']}
                                                                fieldKey={[fieldKey, 'bio']}
                                                                className={styles.formRow}
                                                            >
                                                                <TextArea rows={4} />
                                                            </Form.Item>
                                                            <Form.Item
                                                                label={<FormattedMessage id="player.edit.offers" />}
                                                                name={[name, 'offers']}
                                                                fieldKey={[fieldKey, 'offers']}
                                                                className={styles.formRow}
                                                            >
                                                                <TextArea rows={4} />
                                                            </Form.Item>
                                                            <Form.Item
                                                                label={<FormattedMessage id="player.edit.commits" />}
                                                                name={[name, 'commits']}
                                                                fieldKey={[fieldKey, 'commits']}
                                                                className={styles.formRow}
                                                            >
                                                                <Input maxLength={100} size="large" />
                                                            </Form.Item>
                                                            <Form.Item
                                                                label={
                                                                    <FormattedMessage id="player.edit.jerseyNumber" />
                                                                }
                                                                name={[name, 'jerseyNumbers']}
                                                                fieldKey={[fieldKey, 'jerseyNumbers']}
                                                                className={styles.formRow}
                                                            >
                                                                <Select mode="multiple" className={styles.jersey}>
                                                                    {jerseyNumbersOptions}
                                                                </Select>
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={24} lg={12} className={styles.positionsCol}>
                                                            <Form.Item
                                                                label={
                                                                    <FormattedMessage id="player.edit.sports.positions" />
                                                                }
                                                                name={[name, 'positions']}
                                                                className={styles.positions}
                                                            >
                                                                <Checkbox.Group className="positionsCheckbox">
                                                                    {positionOptions}
                                                                </Checkbox.Group>
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            );
                                        } else {
                                            return <></>;
                                        }
                                    })}
                                </div>
                            )}
                        </Form.List>
                        <Form.Item className={styles.buttons}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                className={styles.save}
                                icon={<Icon component={saveSvg} />}
                                loading={loading === 'saving'}
                            >
                                <FormattedMessage id="settings.button.save" />
                            </Button>
                        </Form.Item>
                    </>
                )}
            </Form>
        );
    };

    renderAcademic = (): React.ReactElement | undefined => {
        const { loading } = this.state;

        const shirtSizeOptions = shirtSizes.map((s) => (
            <Select.Option key={s} value={s}>
                {s}
            </Select.Option>
        ));

        const minGraduationYear = moment().year() - 19;
        const maxGraduationYear = moment().year() + 19;

        return (
            <>
                <header>
                    <h2>
                        <FormattedMessage id="player.edit.academic.info" />
                    </h2>
                </header>

                <div className={styles.form}>
                    <Form
                        ref={this.userAcademicFormRef}
                        onFinish={this.saveUserAcademic}
                        colon={false}
                        layout="vertical"
                        className={styles.form}
                    >
                        <Row gutter={[28, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item label={<FormattedMessage id="player.edit.academic.school" />} name="school">
                                    <Input maxLength={100} size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item name="ncaaRegistered" label={<span />} valuePropName="checked">
                                    <Checkbox className={styles.check}>
                                        <FormattedMessage id="player.edit.academic.ncaa" />
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[28, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item label={<FormattedMessage id="player.edit.academic.grade" />} name="grade">
                                    <InputNumber type="number" size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={<FormattedMessage id="player.edit.academic.graduationYear" />}
                                    name="gradYear"
                                    rules={[
                                        { required: true, message: <FormattedMessage id="status.mandatory" /> },
                                        {
                                            min: minGraduationYear,
                                            type: 'number',
                                            max: maxGraduationYear,
                                            message: `${this.props.intl.formatMessage({
                                                id: 'status.gradYear',
                                            })} ${minGraduationYear} ${this.props.intl.formatMessage({
                                                id: 'and',
                                            })} ${maxGraduationYear}`,
                                        },
                                    ]}
                                >
                                    <InputNumber type="number" min={0} max={maxGraduationYear} size="large" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[28, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item label={<FormattedMessage id="player.edit.academic.act" />} name="act">
                                    <Input maxLength={100} size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label={<FormattedMessage id="player.edit.academic.sat" />} name="sat">
                                    <Input maxLength={100} size="large" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[28, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={<FormattedMessage id="player.edit.academic.coreGpa" />}
                                    name="coreGpa"
                                >
                                    <Input maxLength={100} size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={<FormattedMessage id="player.edit.academic.schoolCredits" />}
                                    name="schoolCredits"
                                >
                                    <Input maxLength={100} size="large" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[28, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={<FormattedMessage id="player.edit.academic.classRank" />}
                                    name="classRankNum"
                                >
                                    <Input maxLength={100} size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={<FormattedMessage id="player.edit.academic.classSize" />}
                                    name="classRankOf"
                                >
                                    <Input maxLength={100} size="large" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={[28, 0]}>
                            <Col xs={24} md={12} className={styles.formRow8}>
                                <Form.Item
                                    label={<FormattedMessage id="signupChild.shirtSize" />}
                                    name="shirtSize"
                                    rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                                >
                                    <Select>{shirtSizeOptions}</Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <h2>
                            <FormattedMessage id="signupChild.teamInfo" />
                        </h2>
                        <Row gutter={[28, 0]}>
                            <Col xs={24} md={12} className={styles.formRow8}>
                                <Form.Item label={<FormattedMessage id="signupChild.team" />} name="clubTeam">
                                    <Input maxLength={100} size="small" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} className={styles.formRow8}>
                                <Form.Item label={<FormattedMessage id="signupChild.teamCoach" />} name="clubTeamCoach">
                                    <Input maxLength={100} size="small" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={[28, 0]}>
                            <Col xs={24} md={12} className={styles.formRow8}>
                                <Form.Item
                                    label={<FormattedMessage id="signupChild.teamCoachEmail" />}
                                    name="clubTeamCoachEmail"
                                    rules={[
                                        {
                                            type: 'email',
                                            message: <FormattedMessage id="status.email.invalid" />,
                                        },
                                    ]}
                                >
                                    <Input maxLength={100} size="small" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12} className={styles.formRow8}>
                                <Form.Item
                                    label={<FormattedMessage id="signupChild.teamCoachPhone" />}
                                    name="clubTeamCoachPhone"
                                    rules={[
                                        {
                                            pattern: /^[+]?[\d]{1}[\s]?[\d]{3}-?[\d]{3}-?[\d]{4}$/,
                                            message: <FormattedMessage id="status.phoneNumber" />,
                                        },
                                    ]}
                                >
                                    <Input maxLength={100} size="small" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item className={styles.buttons}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                className={styles.save}
                                icon={<Icon component={saveSvg} />}
                                loading={loading === 'saving'}
                            >
                                <FormattedMessage id="settings.button.save" />
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            </>
        );
    };

    renderStates = (): JSX.Element[] | undefined => {
        const { country } = this.state;

        const usaStates = stateService.listUsaStates();
        const canadianStates = stateService.listCanadianStates();

        if (country === 'US') {
            const stateOptions = usaStates.map((state) => (
                <Select.Option key={state.code} value={state.code}>
                    {state.name}
                </Select.Option>
            ));
            return stateOptions;
        } else if (country === 'CA') {
            const stateOptions = canadianStates.map((state) => (
                <Select.Option key={state.name} value={state.name}>
                    {state.name}
                </Select.Option>
            ));
            return stateOptions;
        }
    };

    renderAccount = (): React.ReactElement | undefined => {
        const { loading, user } = this.state;
        const stripeCustomerPortalUrl = process.env.REACT_APP_STRIPE_CUSTOMER_PORTAL_URL;
        const gendersOptions = genders.map((gender) => (
            <Select.Option key={gender} value={gender}>
                <FormattedMessage id={gender} />
            </Select.Option>
        ));
        const countryOptions = countries.map((c) => (
            <Select.Option key={c} value={c}>
                <FormattedMessage id={c} />
            </Select.Option>
        ));

        return (
            <Form
                ref={this.userAccountFormRef}
                onFinish={this.saveUserAccount}
                colon={false}
                layout="vertical"
                className={styles.form}
            >
                <header>
                    <h2>
                        <FormattedMessage id="player.edit.account.personal" />
                    </h2>
                </header>
                <div className={styles.avatar}>
                    <div className={styles.misc}>
                        <Row gutter={[28, 0]}>
                            <Col xs={24} md={12}>
                                <Form.Item label={<FormattedMessage id="player.edit.account.twitter" />} name="twitter">
                                    <Input maxLength={100} size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={<FormattedMessage id="player.edit.account.instagram" />}
                                    name="instagram"
                                >
                                    <Input maxLength={100} size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={<FormattedMessage id="player.edit.account.facebook" />}
                                    name="facebook"
                                >
                                    <Input maxLength={100} size="large" />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={<FormattedMessage id="player.edit.account.snapchat" />}
                                    name="snapchat"
                                >
                                    <Input maxLength={100} size="large" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                </div>

                <h3>
                    <FormattedMessage id="player.edit.account.info" />
                </h3>
                <div className={styles.form}>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label={<FormattedMessage id="player.edit.account.firstName" />}
                                name="givenName"
                                rules={[
                                    { required: true, message: <FormattedMessage id="status.mandatory" /> },
                                    {
                                        pattern: /^[^!@#$%^&*()+=\\?\\/,.:;{}[\]]+$/,
                                        message: <FormattedMessage id="status.name" />,
                                    },
                                ]}
                            >
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label={<FormattedMessage id="player.edit.account.lastName" />}
                                name="familyName"
                                rules={[
                                    { required: true, message: <FormattedMessage id="status.mandatory" /> },
                                    {
                                        pattern: /^[^!@#$%^&*()+=\\?\\/,.:;{}[\]]+$/,
                                        message: <FormattedMessage id="status.name" />,
                                    },
                                ]}
                            >
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={6}>
                            <Form.Item
                                label={<FormattedMessage id="player.edit.account.birth" />}
                                name="birthday"
                                rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                            >
                                <DatePicker size="large" format="ll" />
                            </Form.Item>{' '}
                            - {}
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item label={<FormattedMessage id="player.edit.account.gender" />} name="gender">
                                <Select>{gendersOptions}</Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label={<FormattedMessage id="player.edit.account.phone" />}
                                name="phone"
                                rules={[
                                    {
                                        pattern: /^[+]?[\d]{1}[\s]?[\d]{3}-?[\d]{3}-?[\d]{4}$/,
                                        message: <FormattedMessage id="status.phoneNumber" />,
                                    },
                                ]}
                            >
                                <Input size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={6} lg={4}>
                            <Form.Item label={<FormattedMessage id="player.edit.account.heightFt" />} name="height">
                                <InputNumber type="number" min={0} max={7} precision={0} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6} lg={4}>
                            <Form.Item
                                label={<FormattedMessage id="player.edit.account.heightInches" />}
                                name="heightInches"
                            >
                                <InputNumber type="number" min={0} max={11} precision={0} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={6} lg={4}>
                            <Form.Item label={<FormattedMessage id="player.edit.account.weightLbs" />} name="weight">
                                <InputNumber type="number" min={0} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={16}>
                            <Form.Item label={<FormattedMessage id="player.edit.account.address" />} name="address">
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="player.edit.account.city" />} name="city">
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[28, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="player.edit.account.country" />} name="country">
                                <Select onChange={(value: CountryType) => this.changeCountry(value)}>
                                    {countryOptions}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="player.edit.account.state" />} name="state">
                                <Select>{this.renderStates()}</Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label={<FormattedMessage id="player.edit.account.zip" />} name="zip">
                                <Input maxLength={100} size="large" />
                            </Form.Item>
                        </Col>
                    </Row>

                    {this.context.auth && user && this.context.auth.id === user.id && (
                        <Row gutter={[28, 0]}>
                            <Col xs={24} md={24} className={styles.subscription}>
                                <div>
                                    <label>
                                        <FormattedMessage id="player.edit.account.subscription" />
                                    </label>
                                </div>
                                {this.context.auth.subscriptionExpired && (
                                    <Space size="large">
                                        <span className={styles.subscriptionExpired}>
                                            <FormattedMessage
                                                id="player.edit.account.subscription.expired"
                                                values={{
                                                    date: (
                                                        <FormattedDate
                                                            value={this.context.auth.subscriptionExpired as any}
                                                            dateStyle="medium"
                                                        />
                                                    ),
                                                }}
                                            />
                                        </span>
                                        {stripeCustomerPortalUrl && (
                                            <a href={stripeCustomerPortalUrl} target="_blank" rel="noreferrer">
                                                <Button icon={<EditOutlined />} />
                                            </a>
                                        )}
                                    </Space>
                                )}
                            </Col>
                        </Row>
                    )}
                </div>

                <Form.Item className={styles.buttons}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        className={styles.save}
                        icon={<Icon component={saveSvg} />}
                        loading={loading === 'saving'}
                    >
                        <FormattedMessage id="settings.button.save" />
                    </Button>
                </Form.Item>
            </Form>
        );
    };

    renderEmail = (): React.ReactElement | undefined => {
        const { loading } = this.state;

        return (
            <Form
                ref={this.userEmailFormRef}
                onFinish={this.saveUserEmail}
                colon={false}
                layout="vertical"
                className={styles.form}
            >
                <Row gutter={[28, 0]}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={<FormattedMessage id="player.edit.account.email" />}
                            name="email"
                            rules={[
                                {
                                    required: true,
                                    message: <FormattedMessage id="status.mandatory" />,
                                },
                                {
                                    type: 'email',
                                    message: <FormattedMessage id="status.email.invalid" />,
                                },
                            ]}
                        >
                            <Input maxLength={100} size="large" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item>
                            <Button
                                type="primary"
                                size="large"
                                shape="round"
                                htmlType="submit"
                                icon={<Icon component={saveSvg} />}
                                className={styles.save}
                                loading={loading === 'savingEmail'}
                            >
                                <FormattedMessage id="player.edit.account.email.button" tagName="span" />
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        );
    };

    renderUsername = (): React.ReactElement | undefined => {
        const { loading, userAccount } = this.state;

        return (
            <Form
                ref={this.userUsernameFormRef}
                onFinish={this.saveUsername}
                colon={false}
                layout="vertical"
                className={styles.form}
            >
                <Row gutter={[28, 0]}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={<FormattedMessage id="player.edit.account.username" />}
                            name="userName"
                            rules={[
                                { required: true, message: <FormattedMessage id="status.mandatory" /> },
                                {
                                    pattern: /^[a-z0-9_.-]{4,32}$/,
                                    message: <FormattedMessage id="status.characters" />,
                                },
                            ]}
                        >
                            <Input maxLength={32} size="large" disabled={!userAccount?.userNameEditable} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Button
                            type="primary"
                            size="large"
                            shape="round"
                            htmlType="submit"
                            icon={<Icon component={saveSvg} />}
                            className={styles.save}
                            disabled={!userAccount?.userNameEditable}
                            loading={loading === 'savingUsername'}
                        >
                            <FormattedMessage id="player.edit.account.username.button" tagName="span" />
                        </Button>
                    </Col>
                </Row>
            </Form>
        );
    };

    renderContent = (desktop: boolean): React.ReactElement | undefined => {
        const { userSport, userSports, sports } = this.state;

        let userSportsOptions = userSports.map((us) => (
            <Select.Option key={us.id!} value={us.id!}>
                {sports?.find((s) => s.id === us.sportId)?.name}
            </Select.Option>
        ));

        userSportsOptions = [
            ...userSportsOptions!,
            userSports.length < sports.length ? (
                <Select.Option key="" value="">
                    {<FormattedMessage id="player.edit.sports.addSport" />}
                </Select.Option>
            ) : (
                <></>
            ),
        ];

        return (
            <>
                {this.renderHeaderMobile(desktop)}
                <Tabs defaultActiveKey="1" type="card" className={styles.tabs}>
                    <Tabs.TabPane
                        tab={
                            <Select
                                size="large"
                                value={userSport?.id}
                                className={styles.sportSelect}
                                onChange={(value) =>
                                    !value ? this.showModalUserSport(true) : this.changeUserSport(value)
                                }
                            >
                                {userSportsOptions}
                            </Select>
                        }
                        key="sports"
                        className={styles.tab}
                        forceRender
                    >
                        {this.renderSports()}
                    </Tabs.TabPane>
                    <Tabs.TabPane
                        tab={<FormattedMessage id="player.edit.academic" />}
                        key="academic"
                        className={styles.tab}
                        forceRender
                    >
                        {this.renderAcademic()}
                    </Tabs.TabPane>
                    <Tabs.TabPane
                        tab={<FormattedMessage id="player.edit.account" />}
                        key="account"
                        className={styles.tab}
                        forceRender
                    >
                        {this.renderEmail()}
                        {this.renderUsername()}
                        {this.renderAccount()}
                    </Tabs.TabPane>
                </Tabs>
            </>
        );
    };

    render() {
        const { loading, userAccount, user } = this.state;
        const playerProps = playerService.getProps(this.props, user);
        const fullName =
            userAccount &&
            `${userAccount?.givenName} ${userAccount?.familyName} - ${this.props.intl.formatMessage({
                id: 'edit',
            })}`;

        return (
            <>
                <HeadMetadata title={fullName} />
                <LayoutComponent
                    page="player-edit"
                    header={this.renderHeaderDesktop}
                    content={this.renderContent}
                    loading={loading === 'initializing'}
                    {...playerProps}
                />
            </>
        );
    }
}
export default injectIntl(PlayerEditPage);

type ParamsType = { id: string };
interface Props extends RouteComponentProps<ParamsType>, WrappedComponentProps {}

interface State {
    user?: User;
    userAcademic?: UserAcademic;
    userAccount?: UserAccount;
    sport?: Sport;
    userSport?: UserSport;
    userSports: UserSport[];
    sports: Sport[];
    sportPositions: Map<number, Position[]>;
    sportPhotos: SportPhoto[];
    modalUserSportVisible?: boolean;
    country?: CountryType;
    loading?:
        | 'loading'
        | 'saving'
        | 'savingEmail'
        | 'savingUsername'
        | 'deleting'
        | 'initializing'
        | 'loadingCustomerSession';
}

interface SportPhoto {
    sportId: number;
    userSportId: number;
    photo?: string;
    file?: any;
}
